import React, { useEffect, useState } from "react";
import { Control, Controller, FieldErrors } from "react-hook-form";
import { StyleSheet, Platform, FlatList } from "react-native";
import Colors from "../../constants/Colors";
import { View, Text } from "../Themed";
import { DatePickerModal } from "react-native-paper-dates";
import moment from "moment";
import Pressable from "../Pressable";

interface InputDateProps {
  control: Control;
  label: string;
  name: string;
  errors: FieldErrors;
  placeholder?: string;
  required?: boolean;
  mode?: "date" | "time" | "datetime";
  minDate?: Date;
  defaultValue?: Date;
  getValues: any;
  setValue: any;
  setError?: (name: string, value: any) => void;
  clearErrors?: (name?: string | string[]) => void;
  autoSet?: boolean;
  isActive?: string | null;
  setActiveField?: (value: string | null) => void;
}

const InputDate: React.FC<InputDateProps> = ({
  control,
  label,
  name,
  errors,
  required = false,
  mode = "date",
  minDate = null,
  defaultValue = new Date(),
  getValues,
  setValue,
  setError,
  clearErrors,
  autoSet = true,
  isActive: isActiveProp,
  setActiveField: setActiveFieldProp,
}) => {
  const parentRef = React.useRef(null);
  const [parentWidth, setParentWidth] = useState(0);
  const [internalActive, setInternalActive] = useState<string | null>(null);

  const isActive = isActiveProp !== undefined ? isActiveProp : internalActive;
  const setActiveField = setActiveFieldProp || setInternalActive;

  const formatJustTime = (date: Date) => {
    if (!date) return "";
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? "pm" : "am";
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes < 10 ? "0" : ""}${minutes} ${ampm}`;
  };

  useEffect(() => {
    if (mode === "time" && minDate) {
      return;
    } else {
      autoSet && setValue(name, defaultValue);
    }
  }, [minDate]);

  const combineDateAndTime = (dateTime1: Date, dateTime2: Date) => {
    return new Date(
      dateTime1.getFullYear(),
      dateTime1.getMonth(),
      dateTime1.getDate(),
      dateTime2.getHours(),
      dateTime2.getMinutes(),
      dateTime2.getSeconds(),
      dateTime2.getMilliseconds()
    );
  };

  function getTimeInSeconds(date: Date) {
    return date.getHours() * 3600 + date.getMinutes() * 60 + date.getSeconds();
  }

  const handleDateChange = (selectedDate: Date | undefined) => {
    if (!selectedDate) return;

    const minTimeInSeconds = minDate ? getTimeInSeconds(minDate) : 0;
    const selectedTimeInSeconds = getTimeInSeconds(selectedDate);

    if (minDate && selectedTimeInSeconds < minTimeInSeconds) {
      setError &&
        setError(name, {
          type: "manual",
          message: "Selected time must be after minimum time",
        });
      setValue(name, minDate);
    } else {
      clearErrors && clearErrors(name);
      setValue(name, selectedDate);
    }
    setActiveField(null);
  };

  const handleDateChangeRef = React.useRef(handleDateChange);
  handleDateChangeRef.current = handleDateChange;

  const onConfirmSingle = React.useCallback((params) => {
    const { date } = params;
    handleDateChangeRef.current(date);
  }, []);

  const onConfirmSingleTime = React.useCallback((params) => {
    const { hours, minutes } = params;
    const time = new Date();
    time.setHours(hours);
    time.setMinutes(minutes);
    time.setSeconds(0);
    time.setMilliseconds(0);
    handleDateChangeRef.current(time);
  }, []);

  const generateTimeIntervals = () => {
    if (minDate) {
      const intervals = [];
      let current = new Date(minDate);
      const startOfDay = new Date(current);
      startOfDay.setHours(6, 0, 0, 0);
      const endOfDay = new Date(current);
      endOfDay.setHours(22, 0, 0, 0);

      if (current < startOfDay) {
        current = startOfDay;
      }

      while (current <= endOfDay) {
        const hours = current.getHours();
        const period = hours < 12 ? "AM" : "PM";
        const formattedHour = hours % 12 === 0 ? 12 : hours % 12;
        const formattedMinutes = current
          .getMinutes()
          .toString()
          .padStart(2, "0");

        intervals.push({
          twentyFourHourTime: `${hours
            .toString()
            .padStart(2, "0")}:${formattedMinutes}`,
          twelveHourTime: `${formattedHour}:${formattedMinutes} ${period}`,
        });

        current.setMinutes(current.getMinutes() + 15);
      }

      return intervals;
    } else {
      const intervals = [];
      let hour = 6;
      let minutes = 0;

      while (hour < 22) {
        const period = hour < 12 ? "AM" : "PM";
        const formattedHour = hour % 12 === 0 ? 12 : hour % 12;
        const formattedMinutes = minutes.toString().padStart(2, "0");

        intervals.push({
          twentyFourHourTime: `${hour
            .toString()
            .padStart(2, "0")}:${formattedMinutes}`,
          twelveHourTime: `${formattedHour}:${formattedMinutes} ${period}`,
        });

        minutes += 15;
        if (minutes === 60) {
          minutes = 0;
          hour += 1;
        }
      }

      return intervals;
    }
  };

  return (
    <Pressable
      style={{
        flex: 1,
        height:
          Object.keys(errors).length === 0
            ? 94
            : Platform.OS === "web"
            ? 90
            : 130,
        marginBottom: 10,
      }}
      onPress={() => setActiveField(isActive === name ? null : name)}
      ref={parentRef}
      onLayout={(event) => {
        const { width } = event.nativeEvent.layout;
        setParentWidth(width);
      }}
    >
      <Controller
        control={control}
        rules={{
          required: required ? "This field is required" : false,
        }}
        render={({ field: { onChange, value } }) => (
          <View style={styles.body}>
            {label?.length > 0 && (
              <View style={styles.labelRow}>
                <Text style={styles.labelText}>{label}</Text>
                {required && <Text style={styles.errorStar}>*</Text>}
              </View>
            )}
            <View style={[styles.input]}>
              {mode === "date" ? (
                <DatePickerModal
                  locale="en"
                  mode="single"
                  visible={isActive === name}
                  onDismiss={() => setActiveField(null)}
                  date={value || defaultValue}
                  onConfirm={onConfirmSingle}
                  saveLabel="Save"
                  label=" "
                />
              ) : null}
            </View>
            <View
              style={[
                styles.input,
                isActive === name ? styles.inputActive : null,
              ]}
            >
              <Text
                style={{
                  zIndex: 1,
                  color: "black",
                  fontSize: 14,
                  position: "absolute",
                  paddingLeft: 14,
                }}
              >
                {mode === "time"
                  ? formatJustTime(getValues(name))
                  : moment.utc(getValues(name)).format("MM/DD/YYYY")}
              </Text>
            </View>
            {isActive === name && mode === "time" && parentWidth ? (
              <FlatList
                data={generateTimeIntervals()}
                renderItem={({ item }) => (
                  <Pressable
                    onPress={() => {
                      const [hour, minute, period] =
                        item.twentyFourHourTime.split(/[: ]/);
                      const hours = parseInt(hour) + (period === "PM" ? 12 : 0);
                      const minutes = parseInt(minute);
                      onConfirmSingleTime({ hours, minutes, period });
                    }}
                    style={
                      value &&
                      item.twentyFourHourTime ===
                        `${value.getHours().toString().padStart(2, "0")}:${value
                          .getMinutes()
                          .toString()
                          .padStart(2, "0")}`
                        ? {
                            backgroundColor: Colors.nuetral_grey_200,
                          }
                        : {
                            backgroundColor: "white",
                          }
                    }
                  >
                    <Text style={{ padding: 10 }}>{item.twelveHourTime}</Text>
                  </Pressable>
                )}
                keyExtractor={(item) => item.twentyFourHourTime}
                contentContainerStyle={{
                  padding: 10,
                  backgroundColor: "white",
                  width: parentWidth,
                  height: 200,
                  zIndex: 4,
                }}
                style={{
                  position: "absolute",
                  top: 70,
                  backgroundColor: "white",
                  borderLeftColor: Colors.nuetral_grey_400,
                  borderLeftWidth: 1,
                  borderRightColor: Colors.nuetral_grey_400,
                  borderRightWidth: 1,
                  borderBottomColor: Colors.nuetral_grey_400,
                  borderBottomWidth: 1,
                  borderBottomLeftRadius: 5,
                  borderBottomRightRadius: 5,
                }}
                showsVerticalScrollIndicator={false}
              />
            ) : null}
          </View>
        )}
        name={name}
      />
      {errors[name] ? (
        <View style={{ marginTop: 20 }}>
          <Text style={styles.errorText}>{errors[name]?.message}</Text>
        </View>
      ) : (
        <View style={styles.space} />
      )}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  body: {
    paddingTop: 4,
    height: 94,
    flex: 1,
    marginBottom: 10,
  },
  input: {
    position: "absolute",
    top: 28,
    height: 39,
    justifyContent: "flex-start",
    alignItems: "center",
    flexDirection: "row",
    backgroundColor: Colors.nuetral_grey_100,
    borderWidth: 1,
    borderRadius: 5,
    borderColor: Colors.nuetral_grey_400,
    width: "100%",
    overflow: "hidden",
  },
  labelText: {
    fontSize: 16,
    paddingBottom: 14,
    height: 23,
  },
  inputActive: {
    borderColor: Colors.primary_500,
  },
  errorText: {
    color: "#d42121",
    fontSize: 12,
    marginTop: 32,
  },
  space: {
    height: 20,
    marginBottom: 10,
  },
  labelRow: {
    flexDirection: "row",
  },
  errorStar: {
    fontSize: 18,
    marginBottom: -2,
    color: "#d42121",
  },
});

export default InputDate;
